import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonMenuButton,
  IonPage,
  IonRow,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonModal
} from "@ionic/react";
import axios from "axios";
import { checkmark, chevronBack, refreshOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { updateAccount } from "../lib/firebase";
import { useBotsStore } from "../stores/bots.store";
import { useGlobalsStore } from "../stores/globals.store";
import { useUsersStore } from "../stores/users.store";
import { BasicStrategy } from "../components/BasicStrategy";

import challengeScene from "../assets/graphics/QPChallenge_Hero.jpg";
import { useHistory, useParams } from "react-router";
import { useLobbiesStore } from "../stores/lobbies.store";
import { calculateProgress, randomizeIcon } from "../utils/botUtils";
import BotListComponent from "../components/BotList";
import CircularProgressBar from "../components/CircularProgressBar";
import UserName from "../components/UserName";
import ChallengeSummary from "../components/ChallengeSummary";
import ConfirmationPage from "./Confirmation";
import { ReactComponent as MenuIcon } from "../assets/graphics/menu.svg";
import { ReactComponent as TradesIcon } from "../assets/graphics/mode_heat.svg";
import { ReactComponent as StreaksIcon } from "../assets/graphics/mode_streaks.svg";

const Challenge: React.FC = () => {
  const lobbyParam = useParams<any>();
  const [page, setPage] = useState(0);
  const [presentAlert] = useIonAlert();
  const [lobbyId, setLobbyId] = useState<string | undefined>(
    lobbyParam ? lobbyParam["0"].slice(1) : undefined
  );
  const [lobby, setActiveChallenge] = useLobbiesStore(state => [
    state.lobbies.find(l => l.id === lobbyId),
    state.setActiveChallenge
  ]);
  const user = useUsersStore(state => state.user);
  const [oponentBots] = useState([1, 2, 3, 4].map(() => randomizeIcon()));

  const [wager, setWager] = useState(3);
  const defaultBot = useGlobalsStore(state => state?.globals?.defaultBotConfig);
  const lobbyBots = useBotsStore(state =>
    state.bots.filter(b => lobby?.bots.includes(b.id))
  );

  const userBot = lobbyBots.find(b => b.user === user?.userId);
  const [bot, setBot] = useState(
    userBot || {
      ...defaultBot,
      img: randomizeIcon(),
      mode: "backtest"
    }
  );

  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const completeChallenge = (exit?: boolean) => {
    const resetState = () => {
      history.goBack();
      if (user && !user?.hasCompletedChallenge) {
        updateAccount(user.userId, { hasCompletedChallenge: true });
      }
      setActiveChallenge(undefined);
      setTimeout(() => {
        setBot({ ...defaultBot, img: randomizeIcon() });
        setWager(3);
        setPage(0);
        setLobbyId(undefined);
      }, 200);
    };
    if (exit) {
      return presentAlert({
        header: "You're about to exit the QuidPro Challenge",
        message:
          "The challenge is still in progress, results won't be available. Are you sure you'd like to proceed?",
        buttons: [
          {
            text: "Cancel",
            role: "cancel"
          },
          {
            text: "Yes, I'm sure",
            handler: () => {
              resetState();
            }
          }
        ]
      });
    } else {
      resetState();
    }
  };

  useEffect(() => {
    if (!bot.defaultBot) {
      setBot({ ...bot, ...defaultBot });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBot]);

  const [presentConfirm, dismissConfirm] = useIonModal(ConfirmationPage, {
    onDismiss: (confirm: boolean) => {
      dismissConfirm();
      if (confirm) {
        createChallenge();
      }
    },
    title: "Confirm Transaction",
    confirmText: "Confirm Transaction",
    contentHtml: `
      <div class="flex flex-col ion-text-center px-16 py-4 text-primary-30">
        <div class="pb-4 mx-auto">You are entering 1 Bot into the QuidPro Challenge</div>
        <div class="mx-auto">Please note: <b>${wager} QUO</b> will be deducted from your wallet</div>
      </div>
    `,
    cssClass: "transparent-modal"
  });

  const createChallenge = () => {
    setPage(4);
    // setPage(page + 1);
    // THE STRUCTURE OF THIS UPDATE IS TO SUPPORT LEGACY BACKEND CODE
    const data = {
      challenge: {
        wager,
        strategy: {
          ...bot,
          user: user?.userId,
          mode: "backtest",
          provisioned: false
        }
      }
    };
    setCreatingChallenge(true);
    axios
      .post(
        "https://us-central1-quidpro-903c6.cloudfunctions.net/createChallenge",
        data
      )
      .then(response => {
        setLobbyId(response.data.lobbyId);
        setActiveChallenge(response.data.lobbyId);
        history.replace(`/challenge/${response.data.lobbyId}`);
        setCreatingChallenge(false);
        setPage(page + 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  const dismiss = () => {
    history.goBack();
  };

  useEffect(() => {
    if (page === 4 && lobby && lobby.started) {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
    if (page === 4 && lobby && lobby.ended) {
      setPage(5);
    }
  }, [lobby, page]);

  useEffect(() => {
    if (page === 0 && lobbyId) {
      setPage(4);
    }
  }, [lobbyId, page]);

  const progress = userBot
    ? calculateProgress(
        userBot.totalTimestamp,
        userBot.backtestConfig?.startTime,
        userBot.backtestConfig?.endTime
      )
    : 0;

  if (lobbyParam[0] && page === 0) {
    return <IonSpinner className="mx-auto" />;
  }

  const quoWager = [3, 5, 10, 25, 50, 100, 250, 500, 1000].filter(
    // where lower that available balance
    w => w <= (user?.quoCoin ?? 0)
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {page > 0 && page < 4 && (
            <IonButtons slot="start">
              <IonButton onClick={() => setPage(page - 1)}>
                <IonIcon className="text-white" icon={chevronBack} />
              </IonButton>
            </IonButtons>
          )}
          <IonTitle className="capitalize font-normal pt-4">
            {page < 5 ? "QuidPro Challenge" : "Challenge Complete!"}
          </IonTitle>
          {page < 5 && (
            <IonButtons slot="end">
              <IonMenuButton
                disabled={
                  creatingChallenge ||
                  (page === 4 && !creatingChallenge && !lobby?.started)
                }
              >
                <MenuIcon />
              </IonMenuButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {page === 0 && (
          <div className="transition-all flex flex-col justify-between h-full">
            <div className="grow">
              <img
                src={challengeScene}
                alt="Challenge Hero Scene"
                className="mx-auto md:max-h-[420px]"
              />
              <div className="px-10 pt-4 text-lg ion-text-center text-primary-20">
                Win instant QUO prizes through rapid-fire trading challenges
                against other simulated bots
              </div>
            </div>
            <div className="mb-[36px]">
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  color="primary"
                  className="h-16 text-base"
                  expand="block"
                  onClick={() => setPage(page + 1)}
                >
                  Set Challenge
                </IonButton>
              </div>
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  fill="outline"
                  className="text-white h-16 text-base"
                  expand="block"
                  onClick={history.goBack}
                >
                  Cancel
                </IonButton>
              </div>
            </div>
          </div>
        )}
        {page === 1 && (
          <div className="transition-all flex flex-col justify-between h-full">
            <div className="grow">
              <div className="ion-text-center text-2xl w-3/5 mx-auto py-4 font-semibold pt-12">
                What is your risk Appetite?
              </div>
              <div className="ion-text-center">
                Your current Balance:{" "}
                <span className="text-primary font-bold">
                  {user?.quoCoin?.toFixed(2) || 0} QUO
                </span>
              </div>
              <IonGrid className="pt-8 px-2">
                <IonRow className="flex justify-center">
                  {quoWager.map((w, index) => (
                    <IonCol key={index} size="4">
                      <IonButton
                        expand="block"
                        className={`h-32 font-semibold ${
                          wager !== w && "opacity-30"
                        }`}
                        color="tertiary"
                        onClick={() => setWager(w)}
                      >
                        <div>
                          <div className="text-4xl">{w}</div>
                          <div className="text-2xl">QUO</div>
                        </div>
                      </IonButton>
                    </IonCol>
                  ))}
                </IonRow>
              </IonGrid>
            </div>
            <div className="mb-[36px]">
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  color="primary"
                  className="h-16 text-base"
                  expand="block"
                  onClick={() => setPage(page + 1)}
                >
                  Continue
                </IonButton>
              </div>
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  fill="outline"
                  className="text-white h-16 text-base"
                  expand="block"
                  onClick={dismiss}
                >
                  Cancel
                </IonButton>
              </div>
            </div>
          </div>
        )}
        {page === 2 && (
          <div className="flex flex-col h-full">
            <div className="grow px-2">
              <div className="ion-text-center text-2xl px-10 py-4 font-semibold">
                Set your strategy
              </div>
              <div className="px-4">
                <BasicStrategy bot={bot} setBot={setBot} />
              </div>
            </div>
            <div className="mb-24">
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  color="primary"
                  className="h-16 text-base"
                  expand="block"
                  onClick={() => setPage(page + 1)}
                >
                  Set Challenge
                </IonButton>
              </div>
              <div className="ion-text-center py-2 px-4">
                <IonButton
                  fill="outline"
                  className="text-white h-16 text-base"
                  expand="block"
                  onClick={dismiss}
                >
                  Cancel
                </IonButton>
              </div>
            </div>
          </div>
        )}
        {page === 3 && (
          <div className="flex flex-col h-5/6">
            <div className="grow">
              <div className="ion-text-center text-2xl px-10 py-4">
                Challenge Accepted
              </div>
              <div className="flex justify-center">
                <IonAvatar className="bg-gray-800 my-5 w-32 h-32">
                  <div className="relative">
                    <IonButton
                      fill="clear"
                      className="bg-gray-900 rounded-full absolute right-0 w-14 -mr-2"
                      onClick={() => setBot({ ...bot, img: randomizeIcon() })}
                    >
                      <IonIcon
                        icon={refreshOutline}
                        className="text-2rem text-white"
                      />
                    </IonButton>
                  </div>
                  <IonImg src={bot?.img} />
                </IonAvatar>
              </div>
              <div className="flex justify-center pt-4 mr-4">
                {oponentBots.map((url: any, index) => (
                  <IonAvatar
                    key={`avatar-map-${index}`}
                    className="h-14 w-14 bg-gray-800 -mr-4 border-2 "
                  >
                    <IonImg src={url} alt={`icon-${index}`} />
                  </IonAvatar>
                ))}
              </div>
              <div className="flex justify-center text-2xl font-bold pt-4">
                4 Simulated Bots
              </div>
              <div className="flex justify-center ">
                <div className="ion-text-center text-base pt-4 w-4/6">
                  have accepted your challenge @{wager}QUO, making a grand prize
                  of
                </div>
              </div>
              <div className="flex justify-center ion-text-center text-4xl text-primary font-semibold py-8">
                {wager * 5} QUO
              </div>
            </div>

            <div>
              {/* <div className="px-12 text-sm text-white py-3">
                By clicking "Let's Trade" you agree to the entry into the
                challenge at a cost of <b>{wager} QUO</b>
              </div> */}
              <div className="fixed bottom-8 w-screen p-4">
                <IonButton
                  className="h-16 text-base"
                  color="primary"
                  expand="block"
                  onClick={() => presentConfirm()}
                  disabled={creatingChallenge}
                >
                  Next
                </IonButton>
              </div>
            </div>
          </div>
        )}

        {page === 4 && (
          <div className="h-full">
            <div className="flex flex-col items-center bg-primary-90">
              <div className="pt-40 w-full bg-blue-500 bg-gradient-to-b from-primary-90 to-primary-70"></div>
              <div className="flex flex-col items-center">
                <IonAvatar className="h-40 w-40 p-4 -mt-20 bg-primary-90 relative">
                  <IonImg src={bot?.img} />
                  <div className="absolute inset-0">
                    <CircularProgressBar percentage={progress} sqSize={160} />
                  </div>
                </IonAvatar>
                <div className="text-3xl font-bold pt-2">{bot.name}</div>
                {user && <UserName userId={user?.userId} />}
              </div>
              <div className="flex flex-col w-full items-center mt-5">
                <div className="flex w-full justify-around">
                  {["profit", "trades", "streak"].map((key, index) => (
                    <div
                      key={index}
                      className=" uppercase text-xs text-gray-400"
                    >
                      {key}
                    </div>
                  ))}
                </div>
                <div className="flex justify-around w-full mt-2">
                  <div
                    className={`flex items-center justify-center bg-opacity-70 px-8 rounded-lg w-1/3 mx-2 text-center ${
                      userBot && userBot?.profit < 0
                        ? "bg-red-800 text-red-400"
                        : "bg-green-800 text-green-400"
                    }`}
                  >
                    <span className="font-bold">
                      {userBot?.profit?.toFixed(2) || 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-center bg-orange-800 bg-opacity-70 px-8 rounded-lg text-orange-500 w-1/3 mx-2 text-center">
                    <div className="bg-[#ff7f0a] p-[2px] rounded h-4 mr-2">
                      <TradesIcon />
                    </div>
                    <span className="font-bold">
                      {userBot?.tradeCount || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-center bg-blue-800 bg-opacity-70 px-2 rounded-lg text-blue-500 w-1/3 mx-2 text-center py-2">
                    <div className="bg-blue-600 p-[2px] p rounded h-4 mr-2">
                      <StreaksIcon />
                    </div>
                    <span className="font-bold">
                      {userBot?.profitStreak || 0} /{" "}
                      {userBot?.maxProfitStreak || 0}
                    </span>
                  </div>
                </div>
              </div>
              {/* Loading indicator */}
              {loading ? (
                <div className="flex flex-col mt-8 w-full px-4">
                  <div className="flex items-center w-full border-b border-gray-700 py-2">
                    <IonIcon
                      icon={checkmark}
                      className="bg-green-500 rounded-full text-primary-80 mr-2 w-5 h-5"
                    />
                    Challenge Accepted!
                  </div>
                  <div className="flex items-center w-full py-2">
                    {lobby?.started ? (
                      <IonIcon
                        icon={checkmark}
                        className="bg-green-500 rounded-full text-primary-80 mr-2 w-5 h-5"
                      />
                    ) : (
                      <IonSpinner
                        className="bg-green-500 rounded-full text-primary-80 mr-2 w-5 h-5"
                        name="crescent"
                      />
                    )}
                    Setting up
                  </div>
                  <div className="text-sm pl-8 border-b border-gray-700 pb-2">
                    <div className="flex justify-between">
                      Debiting {wager} QUO from your account
                      <IonIcon
                        icon={checkmark}
                        className="text-lg text-green-500"
                      />
                    </div>
                    <div
                      className={
                        "flex justify-between " +
                        (creatingChallenge ? "text-gray-600" : "")
                      }
                    >
                      Initialising the challenge
                      <IonIcon
                        icon={checkmark}
                        className={
                          "text-lg " +
                          (!creatingChallenge ? "text-green-500" : "")
                        }
                      />
                    </div>
                    <div
                      className={
                        "flex justify-between " +
                        (!lobby?.started ? "text-gray-600" : "")
                      }
                    >
                      Populating Historical Trade data
                      <IonIcon
                        icon={checkmark}
                        className={
                          "text-lg " + (lobby?.started ? "text-green-500" : "")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center w-full border-b border-gray-700 py-2">
                    <IonIcon
                      icon={checkmark}
                      className={
                        "rounded-full text-primary-80 mr-2 w-5 h-5 " +
                        (!creatingChallenge && lobby?.started
                          ? "bg-green-500"
                          : "bg-gray-600")
                      }
                    />
                    Challenge Started!
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-xl px-4 py-2 font-bold">Standings</div>
                  {lobby && <BotListComponent lobbyId={lobby.id} />}
                </div>
              )}
            </div>
          </div>
        )}
        {page === 5 && (
          <div className="h-full">
            <ChallengeSummary
              lobby={lobby}
              completeChallenge={completeChallenge}
            />
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Challenge;
